import mustache from "mustache";
import { BaseRequest } from "../../../../../base/request/BaseRequest";
import { serviceProvider } from "../../../../../comun/provider/ServiceProvider";
import { ClienteFincaLoteResponse } from "../../../../../modulos/terceros/response/ClienteFincaLoteResponse";
import { fx_createLoteGrpcService } from "../../../../../modulos/terceros/services/LoteGrpcService";
import { ILoteSelectorField } from "./ILoteSelectorField";
import { LotePickerModalRender } from "../modal/LotePickerModalRender";
import { LoteSelectorConfig } from "./LoteSelectorConfig";
import { LoteSelectorValidation } from "./LoteSelectorValidation";
import { createLoteInfoRender } from "../loteInfo/createLoteInfoRender";
import template from "./loteSelectorTemplate.html";

mustache.parse(template);

/** Contenido cuando no hay lote seleccionado. */
const EMPTY_HTML = '<div class="sx-lote-selector-empty">Sin seleccionar</div>';

/** Contador para ids únicos por instancia. */
let LOTE_SELECTOR_ID = 0;

/** Campo selector de lote/potrero (`IFormField`, sin label). Muestra la ficha del
 *  lote elegido (o "Sin seleccionar") y un botón que abre el picker con mapa; al
 *  elegir, escribe el `loteId` en el request y muestra la ficha. */
export class LoteSelectorField<TRequest extends BaseRequest>
  implements ILoteSelectorField
{
  /** Id único de la instancia (para los ids del DOM). */
  private readonly _fieldId: number;
  private readonly _request: TRequest;
  private readonly _config: LoteSelectorConfig<TRequest>;
  private readonly _validation: LoteSelectorValidation;
  private _loteSelected: ClienteFincaLoteResponse | null;

  constructor(
    request: TRequest,
    config: LoteSelectorConfig<TRequest>,
    validation: LoteSelectorValidation,
  ) {
    LOTE_SELECTOR_ID += 1;
    this._fieldId = LOTE_SELECTOR_ID;
    this._request = request;
    this._config = config;
    this._validation = validation;
    this._loteSelected = config.loteSelected ?? null;
  }

  getId(): string {
    return `lote-selector-${this._fieldId}`;
  }

  /** loteId actualmente seleccionado (desde el request). */
  getValue(): string {
    return this._config.get(this._request);
  }

  render(): string {
    return mustache.render(template, {
      id: this._fieldId,
      content: this._loteSelected
        ? createLoteInfoRender(this._loteSelected).render()
        : EMPTY_HTML,
    });
  }

  connect(): void {
    this._content = document.getElementById(
      `lote-selector-content-${this._fieldId}`,
    );
    this._error = document.getElementById(
      `lote-selector-error-${this._fieldId}`,
    );
    this._btn = document.getElementById(
      `lote-selector-btn-${this._fieldId}`,
    ) as HTMLButtonElement | null;
    this._btn?.addEventListener("click", this._onPick);

    void this._resolveInitialLote();
  }

  /** Al editar: si el request ya trae un loteId pero no se pasó `loteSelected`,
   *  resuelve el lote desde los lotes de la finca (por id) y muestra su ficha. */
  private async _resolveInitialLote(): Promise<void> {
    if (this._loteSelected) return;
    const loteId = this._config.get(this._request);
    if (!loteId) return;

    const finca = serviceProvider.getFincaSelectedService().getRequired();
    const lotes = await fx_createLoteGrpcService(
      finca.id,
    ).getListAsync();
    const lote = lotes.find((l) => l.id === loteId);
    if (!lote || !this._content?.isConnected) return;

    this._loteSelected = lote;
    this._content.innerHTML = createLoteInfoRender(lote).render();
    this._config.onSelect?.(lote);
  }

  dispose(): void {
    this._btn?.removeEventListener("click", this._onPick);
  }

  getError(): string | null {
    if (this._validation.isRequired && this._config.get(this._request) === "") {
      const termino = serviceProvider
        .getFincaSelectedService()
        .getRequired().isGanaderia
        ? "potrero"
        : "lote";
      return `Debe seleccionar ${termino}`;
    }
    return null;
  }

  showError(): void {
    if (this._error) this._error.innerHTML = this.getError() ?? "";
  }

  focus(): void {
    this._btn?.focus();
  }

  disabled(): void {
    if (this._btn) this._btn.disabled = true;
  }

  enabled(): void {
    if (this._btn) this._btn.disabled = false;
  }

  private _content: HTMLElement | null = null;
  private _error: HTMLElement | null = null;
  private _btn: HTMLButtonElement | null = null;

  /** Abre el picker; al elegir, escribe el loteId y muestra la ficha del lote. */
  private _onPick = (): void => {
    new LotePickerModalRender(
      this._config.title,
      (lote) => {
        this._loteSelected = lote;
        this._config.set(this._request, lote.id);
        if (this._content) {
          this._content.innerHTML = createLoteInfoRender(lote).render();
        }
        this.showError();
        this._config.onSelect?.(lote);
      },
      this._loteSelected?.id,
      this._config.filter,
    ).init();
  };
}
